import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { ModuleList } from './ModuleList';
import { ModuleForm } from './ModuleForm';
import { LessonForm } from './LessonForm';
import { updateCourseModules } from '../../../services/admin/courseModuleService';
import { toast } from 'react-hot-toast';
import type { CourseModule, CourseLesson } from '../../../types/course';

interface ModuleManagerProps {
  courseId: string;
  modules: CourseModule[];
  onModulesChange?: (modules: CourseModule[]) => void;
}

export function ModuleManager({ courseId, modules: initialModules, onModulesChange }: ModuleManagerProps) {
  const [modules, setModules] = useState<CourseModule[]>(initialModules);
  const [isModuleFormOpen, setIsModuleFormOpen] = useState(false);
  const [isLessonFormOpen, setIsLessonFormOpen] = useState(false);
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
  const [editingModule, setEditingModule] = useState<CourseModule | null>(null);
  const [editingLesson, setEditingLesson] = useState<CourseLesson | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSaveChanges = async (updatedModules: CourseModule[]) => {
    setIsLoading(true);
    try {
      await updateCourseModules(courseId, updatedModules);
      setModules(updatedModules);
      onModulesChange?.(updatedModules);
      toast.success('Changes saved successfully');
    } catch (error) {
      console.error('Error saving changes:', error);
      toast.error('Failed to save changes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddModule = async (moduleData: Partial<CourseModule>) => {
    setIsLoading(true);
    const newModule = {
      id: crypto.randomUUID(),
      ...moduleData,
      lessons: [],
      completed: false,
      status: 'draft'
    } as CourseModule;

    try {
      const updatedModules = [...modules, newModule];
      await handleSaveChanges(updatedModules);
      setIsModuleFormOpen(false);
    } catch (error) {
      console.error('Error adding module:', error);
      toast.error('Failed to add module');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateModule = async (moduleId: string, updates: Partial<CourseModule>) => {
    const updatedModules = modules.map(module =>
      module.id === moduleId ? { ...module, ...updates } : module
    );
    await handleSaveChanges(updatedModules);
    setEditingModule(null);
    setIsModuleFormOpen(false);
  };

  const handleAddLesson = async (moduleId: string, lessonData: Partial<CourseLesson>) => {
    setIsLoading(true);
    const updatedModules = modules.map(module => {
      if (module.id === moduleId) {
        return {
          ...module,
          lessons: [...module.lessons, {
            id: crypto.randomUUID(),
            ...lessonData,
            completed: false,
            version: 1
          } as CourseLesson]
        };
      }
      return module;
    });

    try {
      await handleSaveChanges(updatedModules);
      setIsLessonFormOpen(false);
      setSelectedModuleId(null);
    } catch (error) {
      console.error('Error adding lesson:', error);
      toast.error('Failed to add lesson');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateLesson = async (moduleId: string, lessonId: string, updates: Partial<CourseLesson>) => {
    const updatedModules = modules.map(module => {
      if (module.id === moduleId) {
        return {
          ...module,
          lessons: module.lessons.map(lesson =>
            lesson.id === lessonId ? { ...lesson, ...updates } : lesson
          )
        };
      }
      return module;
    });

    await handleSaveChanges(updatedModules);
    setEditingLesson(null);
    setIsLessonFormOpen(false);
  };

  return (
    <div className="space-y-6">
      {isLoading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-lg shadow-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-green"></div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium text-white">Course Modules</h2>
        <button
          onClick={() => {
            setEditingModule(null);
            setIsModuleFormOpen(true);
          }}
          className={`btn-primary flex items-center ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          disabled={isLoading}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Module
        </button>
      </div>

      <ModuleList
        modules={modules}
        onAddLesson={(moduleId) => {
          setSelectedModuleId(moduleId);
          setEditingLesson(null);
          setIsLessonFormOpen(true);
        }}
        onEditModule={(module) => {
          setEditingModule(module);
          setIsModuleFormOpen(true);
        }}
      />

      {/* Module Form Modal */}
      {isModuleFormOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-background-card rounded-lg max-w-2xl w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-800">
              <h2 className="text-xl font-bold text-white">
                {editingModule ? 'Edit Module' : 'Add New Module'}
              </h2>
              <button 
                onClick={() => {
                  setIsModuleFormOpen(false);
                  setEditingModule(null);
                }}
                className="text-gray-400 hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              <ModuleForm
                onSubmit={editingModule 
                  ? (data) => handleUpdateModule(editingModule.id, data)
                  : handleAddModule
                }
                initialData={editingModule}
                onCancel={() => {
                  setIsModuleFormOpen(false);
                  setEditingModule(null);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Lesson Form Modal */}
      {isLessonFormOpen && selectedModuleId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-background-card rounded-lg max-w-2xl w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-800">
              <h2 className="text-xl font-bold text-white">
                {editingLesson ? 'Edit Lesson' : 'Add New Lesson'}
              </h2>
              <button 
                onClick={() => {
                  setIsLessonFormOpen(false);
                  setEditingLesson(null);
                  setSelectedModuleId(null);
                }}
                className="text-gray-400 hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              <LessonForm
                onSubmit={editingLesson
                  ? (data) => handleUpdateLesson(selectedModuleId, editingLesson.id, data)
                  : (data) => handleAddLesson(selectedModuleId, data)
                }
                initialData={editingLesson}
                onCancel={() => {
                  setIsLessonFormOpen(false);
                  setEditingLesson(null);
                  setSelectedModuleId(null);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}